import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Percent, ClockCounterClockwise, Info, ArrowClockwise, Check, X, Tray,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import {
  getBranchNames, getGroupCode, getTickets, getJournalVoucherNumbers,
  getJournalVoucherDetails, updateSubscriberInterestAndTds, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { pickerOnly } from '../utils/pickerOnly';

// Kept as-is so earlier audit entries stay grouped with new ones.
const CHANGE_TYPE = 'CHIT_ADVANCE_INTEREST_TDS_PERCENTAGE_REVERSE';

const COLUMNS = [
  { key: 'contact_mailing_name', label: 'Subscriber Name' },
  { key: 'effected_jv_number', label: 'Effected JV No' },
  { key: 'journal_voucher_date', label: 'JV Date' },
  { key: 'interest_month', label: 'Interest Month' },
  { key: 'paid_days', label: 'Paid Days' },
  { key: 'interest_effected_amount', label: 'Interest Effected Amount' },
  { key: 'ledger_amount', label: 'Ledger Amount' },
  { key: 'tbl_trans_journal_voucher_id', label: 'JV Id' },
  { key: 'tbl_trans_journal_voucher_details_id', label: 'JV Details Id' },
];

const INTEREST_RATE = 0.18;
const TDS_RATE = 0.10;
const DAYS_IN_YEAR = 365;

function computeInterestBreakdown(interestRow) {
  if (!interestRow) return { totalAmount: 0, tdsAmount: 0, amount: 0 };

  const baseAmount = Number(interestRow.interest_effected_amount) || 0;
  const paidDays = Number(interestRow.paid_days) || 0;

  const totalAmount = Math.round(baseAmount * INTEREST_RATE * (paidDays / DAYS_IN_YEAR));
  const tdsAmount = Math.round(totalAmount * TDS_RATE);
  const amount = Math.round(totalAmount - tdsAmount);

  return { totalAmount, tdsAmount, amount };
}

function formatCell(key, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'journal_voucher_date') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
  }
  return String(value);
}

function splitInterestAndTdsRows(rows) {
  let interestRow = rows.find(
    (r) => Math.abs(Number(r.ledger_amount) - Number(r.interest_effected_amount)) < 0.01,
  );
  let tdsRow = rows.find((r) => r !== interestRow);

  if (!interestRow) {
    const sorted = [...rows].sort((a, b) => Number(b.ledger_amount) - Number(a.ledger_amount));
    [interestRow, tdsRow] = sorted;
  }

  return { interestRow, tdsRow };
}

function ChitAdvanceInterestTDSPercentageForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [jvNumbers, setJvNumbers] = useState([]);
  const [jvDetails, setJvDetails] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [jvNumber, setJvNumber] = useState('');
  const [reason, setReason] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingJvNumber, setPendingJvNumber] = useState('');
  const [pendingInterestAmount, setPendingInterestAmount] = useState(null);
  const [pendingTdsAmount, setPendingTdsAmount] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  const noDataFound = (what) => showToast('warn', 'No Data Found', `No ${what} found for the current selection.`);

  useEffect(() => {
    getBranchNames()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setBranchlist(list);
        if (list.length === 0) noDataFound('branches');
      })
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromJv = () => {
    setJvDetails([]);
  };

  const resetFromTicket = () => {
    setJvNumber('');
    setJvNumbers([]);
    resetFromJv();
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTickets([]);
    setReceiptDate('');
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
  };

  const handleCaoChange = async (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;

    const selected = branchlist.find((x) => x.branch_name === value);
    if (!selected) return;

    setBranchschema(selected.branch_code);

    try {
      const res = await getGroupCode(selected.branch_code);
      const list = Array.isArray(res) ? res : [];
      setBranchCodeList(list);
      if (list.length === 0) noDataFound('group codes');
    } catch (err) {
      console.error('[getGroupCode] error =', err);
      setBranchCodeList([]);
      showToast('error', 'Load Failed', 'Failed to load group codes. Please try again.');
    }
  };

  const handleGroupChange = async (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;

    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(branchschema, selected.group_Code);
      const list = Array.isArray(res) ? res : [];
      setTickets(list);
      if (list.length === 0) noDataFound('tickets');
    } catch (err) {
      console.error('[getTickets] error =', err);
      setTickets([]);
      showToast('error', 'Load Failed', 'Failed to load tickets. Please try again.');
    }
  };

  const handleTicketChange = (value) => {
    setTicket(value === '' || value === null || value === undefined ? '' : value);
    resetFromTicket();
  };

  const handleReceiptDateChange = (value) => {
    setReceiptDate(value || '');
    resetFromTicket();
  };

  const toJvOptions = (res) => {
    const list = Array.isArray(res) ? res : [];
    const seen = new Set();
    return list
      .map((r) => (r?.journal_voucher_number ?? r?.effected_jv_number ?? r?.Journal_voucher_number ?? ''))
      .map((v) => String(v).trim())
      .filter((v) => v && !seen.has(v) && seen.add(v))
      .map((v) => ({ journal_voucher_number: v }));
  };

  const jvRequestRef = useRef(0);

  const loadJvNumbers = async (ticketValue, dateValue) => {
    const requestId = ++jvRequestRef.current;
    try {
      const res = await getJournalVoucherNumbers(branchschema, groupCode, Number(ticketValue), dateValue);
      if (requestId !== jvRequestRef.current) return;
      const list = toJvOptions(res);
      setJvNumbers(list);
      if (list.length === 0) noDataFound('journal voucher numbers');
    } catch (err) {
      if (requestId !== jvRequestRef.current) return;
      console.error('[getJournalVoucherNumbers] error =', err);
      setJvNumbers([]);
      showToast('error', 'Load Failed', 'Failed to load journal voucher numbers. Please try again.');
    }
  };

  // Journal vouchers on the selected ticket for the chosen chit receipt date (getjournalvouchernumbers).
  useEffect(() => {
    if (!branchschema || !groupCode || ticket === '' || !receiptDate) {
      jvRequestRef.current += 1; // drop any response still in flight
      setJvNumbers([]);
      return;
    }
    loadJvNumbers(ticket, receiptDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchschema, groupCode, ticket, receiptDate]);

  const handleJvChange = async (value) => {
    setJvNumber(value || '');
    resetFromJv();
    if (!value) return;

    try {
      const res = await getJournalVoucherDetails(value, branchschema);
      const list = Array.isArray(res) ? res : [];
      setJvDetails(list);
      if (list.length === 0) noDataFound('journal voucher details');
    } catch (err) {
      console.error('[getJournalVoucherDetails] error =', err);
      setJvDetails([]);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load journal voucher details.'));
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && ticket === '';
  const receiptDateInvalid = touched && !receiptDate;
  const jvNumberInvalid = touched && !jvNumber;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const handleSave = () => {
    setTouched(true);

    if (!cao || !group || ticket === '' || !receiptDate || !jvNumber) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (jvDetails.length < 2) {
      showToast('warn', 'Incomplete Form', 'Expected an interest line and a TDS line for this journal voucher, but found fewer. Please re-select.');
      return;
    }

    if (!Number.isFinite(Number(chitgroupId)) || Number(chitgroupId) <= 0) {
      showToast('warn', 'No Data Found', 'No chit group id found for the selected group. Please re-select.');
      return;
    }

    if (reason.trim().length < 10 || reason.length > 200) {
      showToast('warn', 'Incomplete Form', 'Please enter a reason between 10 and 200 characters.');
      return;
    }

    const { interestRow, tdsRow } = splitInterestAndTdsRows(jvDetails);
    if (!interestRow || !tdsRow) {
      showToast('warn', 'Incomplete Form', 'Could not identify the interest and TDS lines for this journal voucher.');
      return;
    }

    setPendingJvNumber(jvNumber);
    const updated = computeInterestBreakdown(interestRow);
    setPendingInterestAmount(updated.totalAmount);
    setPendingTdsAmount(updated.tdsAmount);
    setPendingReason(reason.trim());

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const confirmSave = async () => {
    setSaving(true);

    const { interestRow, tdsRow } = splitInterestAndTdsRows(jvDetails);
    const updated = computeInterestBreakdown(interestRow);

    const payload = {
      branchSchema: branchschema,
      chitgroupId: Number(chitgroupId),
      ticketNo: Number(ticket),
      interestAmount: updated.totalAmount,
      tdsAmount: updated.tdsAmount,
      journalVoucherId: Number(interestRow.tbl_trans_journal_voucher_id),
      journalVoucherDetailsId1: Number(interestRow.tbl_trans_journal_voucher_details_id),
      journalVoucherDetailsId2: Number(tdsRow.tbl_trans_journal_voucher_details_id),
      paymentVoucherNumber: jvNumber,
    };

    try {
      const res = await updateSubscriberInterestAndTds(payload);
      const message = typeof res === 'string' ? res : res?.message;
      if (message && !/success/i.test(message)) {
        setSaving(false);
        setShowConfirmModal(false);
        showToast('error', 'Update Failed', message, true);
        return;
      }

      const previous = {
        interestAmount: Number(interestRow.ledger_amount),
        tdsAmount: Number(tdsRow.ledger_amount),
      };
      const oldData = { journalVoucherNumber: jvNumber };
      const newData = { journalVoucherNumber: jvNumber };
      Object.keys(previous).forEach((key) => {
        if (previous[key] !== payload[key]) {
          oldData[key] = previous[key];
          newData[key] = payload[key];
        }
      });

      const audit = {
        schemaName: branchschema,
        loginName,
        loginTime: new Date().toISOString(),
        changeType: CHANGE_TYPE,
        oldData,
        newData,
        reason: reason.trim(),
      };

      try {
        await saveData([audit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Subscriber interest and TDS updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Record updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update subscriber interest and TDS. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setShowConfirmModal(false);
    setPendingJvNumber('');
    setPendingInterestAmount(null);
    setPendingTdsAmount(null);
    setPendingReason('');
  };

  const { interestRow: breakdownInterestRow } = jvDetails.length >= 2
    ? splitInterestAndTdsRows(jvDetails)
    : { interestRow: null };

  const { totalAmount, tdsAmount, amount } = useMemo(
    () => computeInterestBreakdown(breakdownInterestRow),
    [breakdownInterestRow],
  );

  const ro = { fontSize: '13px', background: '#F9FAFB' };
  const smLabel = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="chit-advance-interest-tds-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="chit-advance-interest-tds-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Percent size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Advance</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Interest &amp; TDS Percentage</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Journal Voucher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Journal Voucher</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code, ticket, chit receipt date and journal voucher number to load details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Branch Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="chit-advance-interest-tds-branch"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="chit-advance-interest-tds-group"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="chit-advance-interest-tds-ticket"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Chit Receipt Date <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              key={`receipt-date-${formKey}`}
              type="date"
              className="input-field st-datepicker"
              style={{ fontSize: '13px', borderColor: receiptDateInvalid ? '#DC2626' : undefined }}
              value={receiptDate}
              disabled={ticket === ''}
              onChange={(e) => handleReceiptDateChange(e.target.value)}
              {...pickerOnly({ disabled: ticket === '' })}
              data-testid="chit-advance-interest-tds-receipt-date"
            />
            {receiptDateInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Chit Receipt Date is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Journal Voucher No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`jv-${formKey}`}
              options={jvNumbers}
              getLabel={(o) => o.journal_voucher_number}
              getValue={(o) => o.journal_voucher_number}
              value={jvNumber}
              onChange={handleJvChange}
              placeholder={ticket === '' || !receiptDate ? 'Select a ticket and date first' : 'Select journal voucher no'}
              emptyMessage={ticket === '' || !receiptDate ? 'Select a ticket and date first' : 'No journal vouchers for this ticket on this date'}
              disabled={ticket === '' || !receiptDate}
              error={jvNumberInvalid}
              testId="chit-advance-interest-tds-jv"
            />
            {jvNumberInvalid ? (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Journal Voucher No is required</div>
            ) : (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }} data-testid="chit-advance-interest-tds-jv-hint">
                {ticket === '' || !receiptDate
                  ? 'Journal voucher numbers load once a ticket and chit receipt date are selected'
                  : `${jvNumbers.length} journal voucher${jvNumbers.length === 1 ? '' : 's'} on this ticket for this date`}
              </div>
            )}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!jvNumber} hint="Select a journal voucher in section A first">
        {/* SECTION B: Journal Voucher Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Journal Voucher Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Interest and TDS lines for the selected journal voucher</div>
          </div>
        </div>

        {jvDetails.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: '8px', padding: '32px 0', border: '1px dashed #E5E7EB', borderRadius: '8px', color: '#9CA3AF',
          }}
          >
            <Tray size={28} />
            <span style={{ fontSize: '12px' }}>No journal voucher details to display</span>
          </div>
        ) : (
          <div style={{ overflow: 'auto', maxHeight: '280px', border: '1px solid #E5E7EB', borderRadius: '8px', width: 0, minWidth: '100%' }}>
            {/* width:0 + minWidth:100% is what makes this scroll sideways. The box
                sits in a 1fr grid track, which defaults to min-width:auto and so
                stretches to the full width of a table whose cells never wrap, leaving
                overflow nothing to overflow and the right-hand columns off-screen. A
                definite width of zero makes the intrinsic contribution zero while
                minWidth:100% still fills the track. */}
            <table style={{ width: '100%', minWidth: '1080px', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {COLUMNS.map((col) => (
                    <th key={col.key} style={{ position: 'sticky', top: 0, textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280', whiteSpace: 'nowrap', background: '#F9FAFB' }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jvDetails.map((row, idx) => (
                  <tr key={row.tbl_trans_journal_voucher_details_id ?? idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                    {COLUMNS.map((col) => (
                      <td key={col.key} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {formatCell(col.key, row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <div>
            <label style={smLabel}>Total Amount</label>
            <input className="input-field" style={ro} value={totalAmount} readOnly placeholder="—" data-testid="chit-advance-interest-tds-total-amount" />
          </div>
          <div>
            <label style={smLabel}>TDS Amount</label>
            <input className="input-field" style={ro} value={tdsAmount} readOnly placeholder="—" data-testid="chit-advance-interest-tds-tds-amount" />
          </div>
          <div>
            <label style={smLabel}>Amount</label>
            <input className="input-field" style={ro} value={amount} readOnly placeholder="—" data-testid="chit-advance-interest-tds-amount" />
          </div>
        </div>

        </SectionLock>
        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={jvDetails.length < 2} hint="Load the journal voucher details in section B first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="chit-advance-interest-tds-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Minimum 10 characters required'}
          </div>
        )}

        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="chit-advance-interest-tds-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="chit-advance-interest-tds-update-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '460px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="chit-advance-interest-tds-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Percent size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Update</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the subscriber interest and TDS entries for journal voucher
                {' '}
                <strong>{pendingJvNumber}</strong>
                . Please review before confirming.
              </p>

              <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px' }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Interest Amount</p>
                  <p style={{ fontSize: '13px', color: '#111827', fontWeight: '600', margin: 0 }}>{pendingInterestAmount}</p>
                </div>
                <div style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px' }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>TDS Amount</p>
                  <p style={{ fontSize: '13px', color: '#111827', fontWeight: '600', margin: 0 }}>{pendingTdsAmount}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="chit-advance-interest-tds-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="chit-advance-interest-tds-confirm-update">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChitAdvanceInterestTDSPercentageForm;
